import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  HeartIcon, 
  ShareIcon, 
  FlagIcon, 
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  HandRaisedIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  CreditCardIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
  XMarkIcon,
  EnvelopeIcon,
  SparklesIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  PhotoIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import DonationFormModal from "../donation_flow/DonationFormModal";

 import { Helmet } from "react-helmet";

// ─── SEO Component ──────────────────────────────────────────────────────────────
function SEOHead({ campaign, slugWithId, loading }) {
  useEffect(() => {
    if (loading || !campaign) return;
    
    // Page Title
    document.title = `${campaign.title} - DonatePE`;
    
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    
    // Meta Description
    const description = `Support ${campaign.title}. Help raise funds for ${campaign.patient_name || 'medical treatment'} at ${campaign.hospital || 'DonatePE'}. ${campaign.collected_amount ? `₹${campaign.collected_amount.toLocaleString()} raised so far.` : ''} Donate now to make a difference.`;
    setMeta("description", description);
    
    // Robots
    setMeta("robots", "index, follow");
    
    // Charset
    let charset = document.querySelector('meta[charset="UTF-8"]');
    if (!charset) {
      charset = document.createElement("meta");
      charset.setAttribute("charset", "UTF-8");
      document.head.insertBefore(charset, document.head.firstChild);
    }
    
    // Viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.setAttribute("name", "viewport");
      viewport.setAttribute("content", "width=device-width, initial-scale=1.0");
      document.head.appendChild(viewport);
    }
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://donatepe.org/campaign/${slugWithId}`);
    
    // Open Graph Tags
    setMeta("og:title", `${campaign.title} - DonatePE`, true);
    setMeta("og:description", description, true);
    setMeta("og:url", `https://donatepe.org/campaign/${slugWithId}`, true);
    setMeta("og:type", "website", true);
    setMeta("og:image", campaign.cover_image || "https://donatepe.org/logo.png", true);
    
    // Twitter Card Tags
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `${campaign.title} - DonatePE`);
    setMeta("twitter:description", description);
    setMeta("twitter:image", campaign.cover_image || "https://donatepe.org/logo.png");
  }, [campaign, loading, slugWithId]);
  
  return null;
}

// Info Popover Component
const InfoPopover = ({ content, title, position = "top" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const positionClasses = {
    top: 'bottom-full right-0 mb-2',
    bottom: 'top-full right-0 mt-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  };

  const arrowClasses = {
    top: 'absolute -bottom-1 right-3 w-2.5 h-2.5 bg-white border-r border-b border-gray-100 rotate-45',
    bottom: 'absolute -top-1 right-3 w-2.5 h-2.5 bg-white border-l border-t border-gray-100 rotate-45',
    right: 'absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white border-l border-t border-gray-100 rotate-45',
    left: 'absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white border-r border-b border-gray-100 rotate-45',
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-5 h-5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[11px] font-bold flex items-center justify-center hover:scale-110 hover:shadow-md transition-all duration-200 focus:outline-none shadow-sm"
        aria-label="More information"
      >
        i
      </button>
      {isOpen && (
        <div 
          ref={popoverRef}
          className={`absolute z-50 ${positionClasses[position]} w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn`}
        >
          <div className={arrowClasses[position]}></div>
          {title && (
            <div className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500">
              <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
                <InformationCircleIcon className="w-3.5 h-3.5" />
                {title}
              </h4>
            </div>
          )}
          <div className="px-4 py-3">
            <p className="text-xs text-gray-600 leading-relaxed">{content}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Donor Safety Badge Component - Popover opens on the right
const DonorSafetyBadge = () => {
  const [isOpen, setIsOpen] = useState(false);
  const badgeRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target) &&
          badgeRef.current && !badgeRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block">
      <button
        ref={badgeRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full text-xs font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
      >
        <ShieldCheckIcon className="w-4 h-4" />
        Donor Safety Promise
        <svg className="w-3 h-3 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div 
          ref={popoverRef}
          className="absolute z-50 top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn"
        >
          <div className="absolute -top-1 right-4 w-2.5 h-2.5 bg-white border-l border-t border-gray-100 rotate-45"></div>
          <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500">
            <h4 className="text-xs font-semibold text-white flex items-center gap-1.5">
              <ShieldCheckIcon className="w-3.5 h-3.5" />
              Our Promise to You
            </h4>
          </div>
          <div className="px-4 py-3 space-y-2.5">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">Direct Transfer:</span> Donations go directly to hospital/patient accounts</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">Medical Verification:</span> All medical documents verified</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">100% Transparency:</span> Track exactly how your donation is utilized</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">Secure Payments:</span> 256-bit SSL encryption protection</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CampaignDetails = () => {
  const { slugWithId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('story');
  const [donationAmount, setDonationAmount] = useState(500);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currencyRates, setCurrencyRates] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit-card');
  const [showDonationConfetti, setShowDonationConfetti] = useState(false);
  const [currencyRatesLoading, setCurrencyRatesLoading] = useState(true);
  const [currencyRatesError, setCurrencyRatesError] = useState(null);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [topContributors, setTopContributors] = useState([]);
  const [loadingContributors, setLoadingContributors] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [loadingRecentDonations, setLoadingRecentDonations] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  
  const currencyDropdownRef = useRef(null);
  const user = JSON.parse(sessionStorage.getItem("user"));

  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  // Handle navbar hide/show on scroll for mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      
      setShowFloatingButtons(true);
      
      if (currentScrollPos > prevScrollPos && currentScrollPos > 100) {
        setVisible(false);
      } else {
        setVisible(true);
      }
      
      setPrevScrollPos(currentScrollPos);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  // Payment methods
  const paymentMethods = [
    { id: 'credit-card', name: 'Card', icon: CreditCardIcon, color: 'from-blue-500 to-indigo-500', bgLight: 'bg-blue-50' },
    { id: 'upi', name: 'UPI', icon: DevicePhoneMobileIcon, color: 'from-purple-500 to-pink-500', bgLight: 'bg-purple-50' },
    { id: 'netbanking', name: 'NetBanking', icon: BuildingLibraryIcon, color: 'from-emerald-500 to-teal-500', bgLight: 'bg-emerald-50' },
    { id: 'wallet', name: 'Wallet', icon: BanknotesIcon, color: 'from-amber-500 to-orange-500', bgLight: 'bg-amber-50' },
    { id: 'qr', name: 'QR', icon: QrCodeIcon, color: 'from-cyan-500 to-blue-500', bgLight: 'bg-cyan-50' },
  ];

  // Official WhatsApp icon component
  const WhatsAppIcon = (props) => (
  <img 
    src="/assets/whatsapp_icon.png"
    alt="WhatsApp" 
    className={props.className}
    style={props.style}
  />
);

  // Social media platforms with share URLs
  const socialPlatforms = [
    { 
      name: 'Facebook', 
      icon: null, 
      bgColor: 'bg-[#1877F2]', 
      hoverColor: 'hover:bg-[#0e5fc7]', 
      textColor: 'text-white', 
      channel: 'facebook',
      getShareUrl: (url, text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    },
    { 
      name: 'Twitter', 
      icon: null, 
      bgColor: 'bg-black', 
      hoverColor: 'hover:bg-gray-800', 
      textColor: 'text-white', 
      channel: 'twitter',
      getShareUrl: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    },
    { 
      name: 'WhatsApp', 
      icon: WhatsAppIcon, 
      bgColor: 'bg-[#25D366]', 
      hoverColor: 'hover:bg-[#1da85c]', 
      textColor: 'text-white', 
      channel: 'whatsapp',
      getShareUrl: (url, text) => `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
    },
    { 
      name: 'Email', 
      icon: EnvelopeIcon, 
      bgColor: 'bg-gray-600', 
      hoverColor: 'hover:bg-gray-700', 
      textColor: 'text-white', 
      channel: 'email',
      getShareUrl: (url, text) => `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    },
    {
      name: 'LinkedIn',
      icon: null,
      bgColor: 'bg-[#0A66C2]',
      hoverColor: 'hover:bg-[#004182]',
      textColor: 'text-white',
      channel: 'linkedin',
      getShareUrl: (url, text) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    },
    {
      name: 'Telegram',
      icon: null,
      bgColor: 'bg-[#0088cc]',
      hoverColor: 'hover:bg-[#006699]',
      textColor: 'text-white',
      channel: 'telegram',
      getShareUrl: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    }
  ];

  // Tabs
  const tabs = [
    { id: 'story', label: 'Story', icon: DocumentTextIcon },
    { id: 'updates', label: 'Updates', icon: CalendarDaysIcon },
    { id: 'documents', label: 'Documents', icon: ShieldCheckIcon },
  ];

  // Currencies
  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
    { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
    { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
    { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  ];

  // Get initials
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Create dynamic images array
  const campaignImages = useMemo(() => {
    if (!campaign) return [defaultCampaignImage];
    const images = [];
    if (campaign.media_images && campaign.media_images.length > 0) {
      campaign.media_images.forEach(img => {
        if (img.url) {
          const fullUrl = img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`;
          images.push(fullUrl);
        }
      });
    }
    if (campaign.cover_image && !images.includes(campaign.cover_image)) {
      images.unshift(campaign.cover_image);
    }
    if (images.length === 0) {
      images.push(defaultCampaignImage);
    }
    return images;
  }, [campaign]);

  const hasMultipleImages = campaignImages.length > 1;

  const extractCampaignId = (slugWithId) => {
    if (!slugWithId) return null;
    const match = slugWithId.match(/(\d+)$/);
    if (match) return match[1];
    const separators = ['-', '_', '--'];
    for (const separator of separators) {
      if (slugWithId.includes(separator)) {
        const parts = slugWithId.split(separator);
        const lastPart = parts[parts.length - 1];
        if (/^\d+$/.test(lastPart)) return lastPart;
      }
    }
    return slugWithId;
  };

  
const fetchRecentDonations = async () => {
  if (!campaign || !campaign.id) return;
  
  try {
    setLoadingRecentDonations(true);
    
    const response = await fetch(`https://api.donatepe.org/donations/fundraiser/${campaign.id}/recent`);
    
    if (response.ok) {
      const data = await response.json();
      
 
      if (data.success && data.donations && Array.isArray(data.donations) && data.donations.length > 0) {
        const transformedDonations = data.donations.map(donation => ({
          name: donation.donor_name || 'Anonymous',
          amount: parseFloat(donation.amount) || 0,
          time: donation.time || 'Recently',
          message: donation.message || '',
          initials: getInitials(donation.donor_name || 'Anonymous'),
          created_at: donation.created_at
        }));
        setRecentDonations(transformedDonations);
      } else {
        setRecentDonations([]);
      }
    } else {
      
      setRecentDonations([]);
    }
  } catch (error) {
    console.error("[Recent Donations] Error:", error);
    setRecentDonations([]);
  } finally {
    setLoadingRecentDonations(false);
  }
};

  // Track share function
  const trackShare = async (platform) => {
    if (!campaign || !campaign.id) return null;
    
    try {
      const response = await fetch(`${API_BASE_URL}/share/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fundraiserId: campaign.id,
          channel: platform.channel
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const handleShare = async (platform) => {
    if (!campaign) return;
    
    try {
      const shareData = await trackShare(platform);
      
      if (shareData) {
        if (platform.channel === 'whatsapp' && shareData.whatsappUrl) {
          window.open(shareData.whatsappUrl, '_blank');
          return;
        }
        else if (platform.channel === 'email' && shareData.message && shareData.link) {
          const subject = encodeURIComponent(`Help support ${campaign.title}`);
          const body = encodeURIComponent(shareData.message + '\n\n' + shareData.link);
          window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
          return;
        }
        else if (shareData.link) {
          const shareText = encodeURIComponent(shareData.message || `Help support ${campaign.title}`);
          
          switch(platform.channel) {
            case 'facebook':
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.link)}`, '_blank');
              break;
            case 'twitter':
              window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareData.link)}&text=${shareText}`, '_blank');
              break;
            case 'linkedin':
              window.open(`https://www.linkedin.com/sharing/share-offsite/?u=${encodeURIComponent(shareData.link)}`, '_blank');
              break;
            case 'telegram':
              window.open(`https://t.me/share/url?url=${encodeURIComponent(shareData.link)}&text=${shareText}`, '_blank');
              break;
            default:
              window.open(shareData.link, '_blank');
          }
          return;
        }
      }
      
      const shareUrl = `${window.location.origin}/campaign/${campaign.slug}`;
      const shareText = `Help support ${campaign.title}`;
      const fallbackUrl = platform.getShareUrl(shareUrl, shareText);
      window.open(fallbackUrl, '_blank');
      
    } catch (error) {
      const shareUrl = `${window.location.origin}/campaign/${campaign.slug}`;
      const shareText = `Help support ${campaign.title}`;
      const fallbackUrl = platform.getShareUrl(shareUrl, shareText);
      window.open(fallbackUrl, '_blank');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setShowCurrencyDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch top contributors function
  const fetchTopContributors = async (campaignId) => {
    if (!campaignId) return;
    
    try {
      setLoadingContributors(true);
      
      const response = await fetch(
        `${API_BASE_URL}/public/fundraisers/${campaignId}/top-contributors`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.contributors) {
          setTopContributors(data.contributors);
        }
      }
    } catch (error) {
      console.error('Error fetching top contributors:', error);
    } finally {
      setLoadingContributors(false);
    }
  };

  useEffect(() => {
    const fetchCampaignDetails = async () => {
  try {
    setLoading(true);
    setError(null);
    
    if (!slugWithId) {
      throw new Error("No campaign specified in URL");
    }

    const endpoint = `${API_BASE_URL}/public/fundraisers/${slugWithId}`;
    
    
    const response = await fetch(endpoint);
    
   
    
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    const data = await response.json();
    
  

    if (data.success && data.fundraiser) {
      const mediaImages = data.fundraiser.media?.images || [];
      
      let coverImageUrl = defaultCampaignImage;
      if (mediaImages.length > 0 && mediaImages[0].url) {
        const imagePath = mediaImages[0].url.startsWith('/') 
          ? mediaImages[0].url 
          : `/${mediaImages[0].url}`;
        coverImageUrl = `${API_BASE_URL}${imagePath}`;
      }
      
      const supporters = data.fundraiser.donations_count || Math.floor(Math.random() * 300) + 100;
      
      const transformedCampaign = {
        id: data.fundraiser.id,
        title: data.fundraiser.title,
        slug: data.fundraiser.slug,
        goal_amount: data.fundraiser.goal_amount,
        collected_amount: data.fundraiser.collected_amount || 0,
        created_at: data.fundraiser.created_at,
        end_date: data.fundraiser.end_date,
        days_left: data.fundraiser.days_left || 30,
        creator_name: data.fundraiser.creator_name || "Anonymous",
        creator_relationship: "Organizer",
        cover_image: coverImageUrl,
        story: data.fundraiser.story_html ? 
          data.fundraiser.story_html.replace(/<[^>]*>/g, '') : 
          `My name is ${data.fundraiser.creator_name} and I am raising funds for medical treatment.`,
        category: "Medical",
        purpose: data.fundraiser.purpose || data.fundraiser.condition_text || "Medical Treatment",
        hospital: data.fundraiser.hospital || "Hospital",
        location: data.fundraiser.city || "Location",
        is_verified: true,
        tax_exemption: true,
        updates: data.fundraiser.updates || [],
        supporters: supporters,
        patient_name: data.fundraiser.patient_name || "The Beneficiary",
        patient_age: data.fundraiser.age || "Unknown",
        patient_condition: data.fundraiser.condition_text || "Medical Treatment",
        raised_percentage: Math.round((data.fundraiser.collected_amount / data.fundraiser.goal_amount) * 100) || 0,
        status: data.fundraiser.status || "approved",
        basic_details: data.fundraiser.basic_details || {},
        
        media_images: data.fundraiser.media?.images?.map(img => ({
          id: img.id,
          url: img.url.startsWith('http') ? img.url : `${API_BASE_URL}${img.url}`,
          resource_type: img.resource_type
        })) || [],
        
        documents: data.fundraiser.media?.documents?.map(doc => ({
          id: doc.id,
          name: doc.name || `Document ${doc.id}`,
          file_name: doc.url?.split('/').pop() || `document-${doc.id}.pdf`,
          url: doc.url.startsWith('http') ? doc.url : `${API_BASE_URL}${doc.url}`,
          resource_type: doc.resource_type,
          uploaded_at: doc.created_at || data.fundraiser.created_at
        })) || []
      };

      

      setCampaign(transformedCampaign);
      
      fetchTopContributors(transformedCampaign.id);
    }
  } catch (error) {
    console.error("[Campaign Details] Error:", error);
    setError(`Failed to load campaign: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

    const fetchCurrencyRates = async () => {
      try {
        setCurrencyRatesLoading(true);
        const response = await fetch("https://api.frankfurter.app/latest?from=USD");
        const data = await response.json();
        if (data && data.rates) {
          setCurrencyRates(data.rates);
        }
      } catch (error) {
        setCurrencyRates({
          USD: 1, EUR: 0.92, GBP: 0.79, INR: 83.0,
          CAD: 1.35, AUD: 1.52, JPY: 148.0
        });
      } finally {
        setCurrencyRatesLoading(false);
      }
    };

    fetchCampaignDetails();
    fetchCurrencyRates();
  }, [slugWithId]);

  // Separate useEffect to fetch recent donations when campaign is loaded
  useEffect(() => {
    if (campaign && campaign.id) {
      const timer = setTimeout(() => {
        fetchRecentDonations();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [campaign]);

  const convertAmount = (amount, fromCurrency = 'USD', toCurrency = selectedCurrency) => {
    if (!currencyRates || !currencyRates[toCurrency]) return amount;
    const rate = currencyRates[toCurrency] / (currencyRates[fromCurrency] || 1);
    return amount * rate;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(amount);
  };

  const handleDonate = () => {
    setShowDonationForm(true);
  };

  const calculateProgress = () => {
    if (!campaign || !campaign.goal_amount) return 0;
    return Math.min(100, Math.round((campaign.collected_amount / campaign.goal_amount) * 100));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recently";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } catch {
      return "Recently";
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === campaignImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? campaignImages.length - 1 : prevIndex - 1
    );
  };

  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    setShowCurrencyDropdown(false);
  };

  // Get relation text from basic_details
  const getRelationText = () => {
    const relation = campaign?.basic_details?.relation || "";
    if (!relation || relation === "") return "";
    return `${campaign.patient_name} is ${relation} of ${campaign.creator_name}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-3/4"></div>
            <div className="h-[400px] bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleViewDocument = (doc) => {
    if (doc.url) {
      window.open(doc.url, '_blank');
    } else if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {error || "Campaign not found"}
          </h2>
          <button 
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            Browse Campaigns
          </button>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <>
   

<Helmet>
  <title>DonatePe | Raise Funds for Medical & Social Causes in India</title>

  <meta
    name="description"
    content="DonatePe is a trusted crowdfunding platform in India to raise funds for medical emergencies, personal needs, and social causes. Start fundraising or donate easily."
  />

  <meta
    name="keywords"
    content="donate, crowdfunding India, medical fundraiser, charity, donatepe"
  />

  {/* Open Graph */}
  <meta property="og:title" content="DonatePe | Online Fundraising Platform" />
  <meta
    property="og:description"
    content="Raise funds for causes you care about or support others through DonatePe."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://donatepe.org/" />

  {/* Recommended additions */}
  <meta property="og:image" content="https://donatepe.org/preview.png" />
  <link rel="canonical" href="https://donatepe.org/" />
</Helmet>


      <SEOHead campaign={campaign} slugWithId={slugWithId} loading={loading} />
      
      {/* Breadcrumb Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://donatepe.org"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Campaigns",
              "item": "https://donatepe.org/campaigns"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": campaign.title,
              "item": `https://donatepe.org/campaign/${slugWithId}`
            }
          ]
        })}
      </script>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      
      {/* Mobile Navbar - Only Logo, hides on scroll */}
      <nav className={`lg:hidden fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md shadow-sm z-50 transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'} pl-4`}>
        <Link
          to="/"
          className="relative group flex items-center overflow-visible h-14"
        >
          <div className="relative z-20 flex items-center justify-center h-full">
            <img
              src="/assets/donatepe_single_logo.jpg"
              alt="DonatePe Logo"
              className="h-8 w-auto object-contain"
            />
          </div>

          <div className="relative overflow-visible flex items-center h-full ml-1">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-black leading-none whitespace-nowrap pl-1 flex items-end">
              <span className="letter-animation letter-1">D</span>
              <span className="letter-animation letter-2">o</span>
              <span className="letter-animation letter-3">n</span>
              <span className="letter-animation letter-4">a</span>
              <span className="letter-animation letter-5">t</span>
              <span className="letter-animation letter-6">e</span>
              <span className="text-black flex items-end">
                <span className="letter-animation letter-7">P</span>
                <span className="letter-animation letter-8">e</span>
              </span>
              <span className="letter-animation letter-9 text-[0.5em] -ml-1 mb-1">™</span>
            </h1>
          </div>
        </Link>
      </nav>
      
      {showDonationForm && (
        <DonationFormModal
          onClose={() => setShowDonationForm(false)}
          campaign={campaign}
          selectedCurrency={selectedCurrency}
          selectedPaymentMethod={selectedPaymentMethod}
          formatCurrency={formatCurrency}
          showDonationConfetti={showDonationConfetti}
          setShowDonationConfetti={setShowDonationConfetti}
          loggedInUser={user}
        />
      )}

      {/* Floating Action Buttons - Always visible */}
      <div className={`fixed bottom-6 right-4 z-50 flex flex-col gap-3 transition-all duration-300 opacity-100 translate-y-0`}>
        <button
          onClick={() => {
            const whatsappPlatform = socialPlatforms.find(p => p.channel === 'whatsapp');
            handleShare(whatsappPlatform);
          }}
          className="w-14 h-14 rounded-full bg-white shadow-lg hover:shadow-xl flex items-center justify-center hover:scale-110 transition-all duration-300 animate-bounce-slow group relative border border-gray-200"
          title="Share on WhatsApp"
        >
          <WhatsAppIcon className="w-7 h-7" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden lg:block">
            Share on WhatsApp
          </span>
        </button>

        <button
          onClick={handleDonate}
          className="w-14 h-14 rounded-full bg-gradient-to-r from-[#00B7B3] to-teal-500 shadow-lg hover:shadow-xl flex items-center justify-center text-white hover:scale-110 transition-all duration-300 animate-bounce-slow group relative"
          title="Donate Now"
        >
          <HandRaisedIcon className="w-7 h-7" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden lg:block">
            Donate Now
          </span>
        </button>
      </div>

      <main className="container mx-auto px-4 py-8 max-w-7xl lg:pt-8 pt-20">
        
        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="hidden lg:grid lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column - Desktop Storytelling */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Campaign Title with Status Badge and Inactive Banner */}
            <div className="mb-4 text-left">
              <div className="flex items-center gap-3 mb-4 justify-start">
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  {campaign.title}
                </h1>
                {campaign.status === "inactive" && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    Inactive
                  </span>
                )}
              </div>
              
              {/* Inactive Campaign Banner - Desktop */}
              {campaign.status === "inactive" && (
                <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-xl">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-yellow-800">Campaign Inactive</h3>
                      <p className="text-sm text-yellow-700">
                        This campaign is currently inactive and not accepting donations.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Desktop Image Gallery */}
            <div className="relative group rounded-2xl overflow-hidden shadow-2xl border-4 border-white/50">
              <div className="aspect-[16/9]">
                <img
                  src={campaignImages[currentImageIndex]}
                  alt={campaign.title}
                  className="w-full h-full object-cover transition-transform duration-7000 group-hover:scale-110"
                  onError={(e) => e.target.src = defaultCampaignImage}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

              {/* Left Side Badges - Only Verified */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-xs font-medium border border-white/30 shadow-lg flex items-center gap-1 backdrop-blur-sm">
                  <CheckBadgeIcon className="w-3.5 h-3.5" />
                  Verified
                </span>
              </div>

              {/* Right Side Badge - Tax Benefit */}
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1.5 bg-amber-500 text-white rounded-full text-xs font-medium shadow-lg flex items-center gap-1">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                  Tax Benefit
                </span>
              </div>
              
              {hasMultipleImages && (
                <>
                  <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all">
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-all">
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </>
              )}
              
              {hasMultipleImages && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {campaignImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white w-4' 
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Donor Safety Promise Banner - Below Image Desktop - Aligned to Right */}
            <div className="mt-4 flex justify-end">
              <DonorSafetyBadge />
            </div>

            {/* Desktop Tabs */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-1.5 shadow-lg border border-white/50">
              <div className="flex">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                        isActive 
                          ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop Tab Content - Removed Campaign Timeline */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {activeTab === 'story' && (
                <div className="p-8">
                  <div className="relative mb-8">
                    <div className="absolute -top-4 -left-2 text-6xl text-teal-200 opacity-50">"</div>
                    <div className="relative z-10 pl-8">
                      <p className="text-lg text-gray-700 leading-relaxed italic">
                        {campaign.story}
                      </p>
                    </div>
                    <div className="absolute -bottom-10 -right-2 text-6xl text-teal-200 opacity-50 rotate-180">"</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-12">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                          {getInitials(campaign.hospital)}
                        </div>
                        <h4 className="font-semibold text-gray-900">Hospital</h4>
                      </div>
                      <p className="text-gray-700 font-medium text-left">{campaign.hospital}</p>
                      <p className="text-xs text-gray-500 mt-1 text-left">{campaign.location}</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                          {getInitials(campaign.patient_name)}
                        </div>
                        <h4 className="font-semibold text-gray-900">Patient</h4>
                      </div>
                      <p className="text-gray-700 font-medium text-left">{campaign.patient_name}</p>
                      <p className="text-xs text-gray-500 mt-1 text-left">
                        {campaign.patient_age} years • {campaign.patient_condition}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'updates' && (
                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <CalendarDaysIcon className="w-6 h-6 text-teal-600" />
                    Campaign Updates
                  </h3>
                  {campaign.updates?.length > 0 ? (
                    <div className="space-y-4">
                      {campaign.updates.map((update) => (
                        <div key={update.id} className="border-l-4 border-teal-500 pl-4 py-2">
                          <p className="text-sm text-gray-500">{formatDate(update.date)}</p>
                          <p className="font-medium text-gray-900">{update.title}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No updates yet
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'documents' && (
                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <ShieldCheckIcon className="w-6 h-6 text-teal-600" />
                    Documents
                  </h3>
                  
                  {campaign.documents && Array.isArray(campaign.documents) && campaign.documents.length > 0 ? (
                    <div className="space-y-3">
                      {campaign.documents.map((doc) => (
                        <div key={doc?.id || Math.random()} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                            <span className="font-medium">{doc?.name || 'Unnamed document'}</span>
                          </div>
                          <button 
                            onClick={() => handleViewDocument(doc)}
                            className="text-teal-600 text-sm hover:text-teal-800"
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      No documents available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Desktop Donation Card */}
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="sticky top-24 space-y-6 max-w-md mx-auto">
              
              {/* Premium Donation Card */}
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#00B7B3] to-teal-500 px-5 py-3 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10">
                    <svg width="100%" height="100%">
                      <pattern id="pattern-circles" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="2" fill="white" />
                      </pattern>
                      <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-circles)" />
                    </svg>
                  </div>
                  
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                        </span>
                        <span className="text-[10px] font-medium uppercase tracking-wider text-white/80">
                          {campaign.days_left < 5 ? '🔥 URGENT' : '✨ ACTIVE'}
                        </span>
                      </div>
                      
                      <div className="relative" ref={currencyDropdownRef}>
                        <button
                          onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                          className="flex items-center gap-1 bg-white/20 backdrop-blur-sm hover:bg-white/30 px-2 py-1 rounded-full text-xs transition-all border border-white/30"
                        >
                          <span className="text-sm">{currencies.find(c => c.code === selectedCurrency)?.flag}</span>
                          <span className="font-medium">{selectedCurrency}</span>
                          <svg className={`w-3 h-3 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {showCurrencyDropdown && (
                          <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-20 overflow-y-auto max-h-60 w-36 animate-fade-in">
                            {currencies.map((currency) => (
                              <button
                                key={currency.code}
                                onClick={() => handleCurrencyChange(currency.code)}
                                className={`w-full px-3 py-2.5 text-left flex items-center gap-2 transition-all hover:bg-gray-50 ${
                                  selectedCurrency === currency.code ? 'bg-teal-50' : ''
                                }`}
                              >
                                <span className="text-lg">{currency.flag}</span>
                                <span className="text-sm font-medium text-gray-900">{currency.code}</span>
                                {selectedCurrency === currency.code && (
                                  <CheckBadgeIcon className="w-3.5 h-3.5 text-[#00B7B3] ml-auto" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-base font-bold text-white mt-1">Your Support Can Save a Life Today</h3>
                    <p className="text-[10px] text-white/70 mt-0.5">
                      Help {campaign.patient_name || 'the beneficiary'} get the treatment they need
                    </p>
                  </div>
                </div>

                <div className="p-5">
                  
                  {/* Pay With Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                        <CreditCardIcon className="w-3.5 h-3.5 text-[#00B7B3]" />
                        Pay with
                      </span>
                      <span className="text-[9px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                        <ShieldCheckIcon className="w-2.5 h-2.5" />
                        Secure
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-5 gap-1.5">
                      {paymentMethods.map((method) => {
                        const Icon = method.icon;
                        const isSelected = selectedPaymentMethod === method.id;
                        return (
                          <button
                            key={method.id}
                            onClick={() => {
                              setSelectedPaymentMethod(method.id);
                              setShowDonationForm(true);
                            }}
                            className={`relative p-2 rounded-lg border transition-all duration-200 hover:scale-105 group ${
                              isSelected 
                                ? 'border-[#00B7B3] bg-gradient-to-br from-teal-50 to-emerald-50 shadow-sm' 
                                : 'border-gray-200 hover:border-[#00B7B3] hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <Icon className={`w-4 h-4 mb-0.5 ${isSelected ? 'text-[#00B7B3]' : 'text-gray-600 group-hover:text-[#00B7B3]'}`} />
                              <span className={`text-[7px] font-medium ${isSelected ? 'text-[#00B7B3]' : 'text-gray-500'}`}>
                                {method.name}
                              </span>
                            </div>
                            {isSelected && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#00B7B3] rounded-full border-2 border-white flex items-center justify-center">
                                <svg className="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fund Transfer Info - More Attractive with Right-aligned Popover */}
                  <div className="mb-5 px-3 py-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-100 shadow-sm">
                    <div className="flex items-center justify-start gap-2 text-sm font-medium text-gray-700">
                      <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Funds will be transferred for patient treatment</span>
                      <InfoPopover 
                        content="DonatePe prioritizes funds transfer towards hospital/healthcare service provider/medicine procurement, then towards reimbursement of patient's medical expenses and post-treatment expenses."
                        title="How funds are utilized"
                        position="top"
                      />
                    </div>
                  </div>

                  {/* Desktop Donate Button - Always enabled */}
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={handleDonate}
                      className="px-8 py-3 bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 mx-auto"
                    >
                      <HeartIcon className="w-4 h-4" />
                      Donate Now
                    </button>
                  </div>

                  {/* Share Icons Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                        <ShareIcon className="w-4 h-4 text-[#00B7B3]" />
                        Share this campaign
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      {socialPlatforms.slice(0, 3).map((platform) => {
                        const Icon = platform.icon;
                        return (
                          <button
                            key={platform.channel}
                            onClick={() => handleShare(platform)}
                            className="relative py-3 px-1 rounded-xl border-2 border-gray-200 transition-all duration-200 hover:scale-105 hover:shadow-lg group flex flex-col items-center justify-center"
                            title={`Share on ${platform.name}`}
                          >
                            <div className="flex flex-col items-center justify-center w-full">
                              {platform.name === 'Facebook' && (
                                <svg className="w-6 h-6 text-[#1877F2] mb-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                              )}
                              {platform.name === 'Twitter' && (
                                <svg className="w-6 h-6 text-black mb-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231z"/>
                                </svg>
                              )}
                              {platform.name === 'WhatsApp' && Icon && (
                                <Icon className="w-6 h-6 text-[#25D366] mb-1" />
                              )}
                              <span className="text-[9px] font-medium text-gray-600 text-center w-full">{platform.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {socialPlatforms.slice(3, 6).map((platform) => {
                        const Icon = platform.icon;
                        return (
                          <button
                            key={platform.channel}
                            onClick={() => handleShare(platform)}
                            className="relative py-3 px-1 rounded-xl border-2 border-gray-200 transition-all duration-200 hover:scale-105 hover:shadow-lg group flex flex-col items-center justify-center"
                            title={`Share on ${platform.name}`}
                          >
                            <div className="flex flex-col items-center justify-center w-full">
                              {platform.name === 'Email' && Icon && (
                                <Icon className="w-6 h-6 text-gray-600 mb-1" />
                              )}
                              {platform.name === 'LinkedIn' && (
                                <svg className="w-6 h-6 text-[#0A66C2] mb-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                              )}
                              {platform.name === 'Telegram' && (
                                <svg className="w-6 h-6 text-[#0088cc] mb-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.916.49-1.305.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                              )}
                              <span className="text-[9px] font-medium text-gray-600 text-center w-full">{platform.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Progress Circle */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative inline-flex items-center justify-center mb-4">
                      <svg width="200" height="200" viewBox="0 0 200 200" className="transform -rotate-90">
                        <circle cx="100" cy="100" r="88" stroke="#e5e7eb" strokeWidth="12" fill="none" className="opacity-30" />
                        <circle cx="100" cy="100" r="88" stroke="url(#progressGradient)" strokeWidth="12" fill="none" strokeLinecap="round" strokeDasharray={2 * Math.PI * 88} strokeDashoffset={2 * Math.PI * 88 * (1 - progress / 100)} className="transition-all duration-1000 ease-out" />
                        <defs>
                          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#00B7B3" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                        <span className="text-5xl font-bold text-gray-900">{progress}%</span>
                        <span className="text-base text-gray-500 mt-1">funded</span>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <span className="text-4xl font-bold text-gray-900">{formatCurrency(campaign.collected_amount)}</span>
                      <span className="text-base text-gray-500 ml-2">raised</span>
                      <p className="text-lg text-gray-400 mt-2">of {formatCurrency(campaign.goal_amount)} goal</p>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-3 flex items-center justify-center gap-3 text-[8px] text-gray-400">
                    <span className="flex items-center gap-0.5"><ShieldCheckIcon className="w-3 h-3" />SSL</span>
                    <span className="w-0.5 h-0.5 bg-gray-300 rounded-full"></span>
                    <span className="flex items-center gap-0.5"><FlagIcon className="w-3 h-3" />Verified</span>
                    <span className="w-0.5 h-0.5 bg-gray-300 rounded-full"></span>
                    <span className="flex items-center gap-0.5"><span>🔒</span>256-bit</span>
                  </div>
                </div>
              </div>

              {/* Fundraiser Details Card - Updated with Supporter, Beneficiary, Relationship, and Hospital */}

<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
  <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
    <UserCircleIcon className="w-5 h-5 text-[#00B7B3]" />
    Fundraiser Details
  </h3>
  
  <div className="space-y-4">
    {/* Supporter */}
    <div className="flex gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {getInitials(campaign.creator_name)}
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 text-left">Supporter</span>
        <span className="font-medium text-gray-900 text-left">{campaign.creator_name}</span>
      </div>
    </div>
    
    {/* Beneficiary - Name only */}
    <div className="flex gap-3">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {getInitials(campaign.patient_name)}
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 text-left">Beneficiary</span>
        <span className="font-medium text-gray-900 text-left">{campaign.patient_name}</span>
      </div>
    </div>
    
    {/* Relationship & Hospital - Continuous line without dot */}
    <div className="mt-2 pt-2 border-t border-gray-100">
      <p className="text-xs text-gray-700 leading-relaxed">
        {getRelationText()} Getting treatment at <span className="font-semibold">{campaign.hospital}</span>, {campaign.location}
      </p>
    </div>
  </div>
</div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <SparklesIcon className="w-5 h-5 text-amber-500" />
                  Top Contributors
                </h3>
                
                {loadingContributors ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-10 bg-gray-200 rounded"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                ) : topContributors.length > 0 ? (
                  <div className="space-y-3">
                    {topContributors.slice(0, 3).map((contributor, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' : 
                            index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' : 
                            'bg-gradient-to-br from-amber-700 to-orange-600'
                          }`}>
                            {getInitials(contributor.donor_name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{contributor.donor_name}</p>
                            {index === 0 && <p className="text-[10px] text-amber-600">Top Donor</p>}
                          </div>
                        </div>
                        <span className="font-bold text-[#00B7B3] text-sm">
                          {formatCurrency(parseFloat(contributor.total_amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 text-sm py-4">No contributors yet</p>
                )}
              </div>

              {/* Recent Donations */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <HeartIcon className="w-5 h-5 text-rose-500" />
                  Recent Support
                </h3>
                
                {loadingRecentDonations ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-start gap-3 animate-pulse">
                        <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-16"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : recentDonations.length > 0 ? (
                  <div className="space-y-4">
                    {recentDonations.slice(0, 5).map((donation, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {donation.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-900 truncate">{donation.name}</p>
                            <span className="text-xs font-bold text-[#00B7B3] flex-shrink-0 ml-2">{formatCurrency(donation.amount)}</span>
                          </div>
                          <p className="text-xs text-gray-500">{donation.time}</p>
                          {donation.message && (
                            <p className="text-xs text-gray-600 mt-1 italic truncate">"{donation.message}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <HeartIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No donations yet</p>
                    <p className="text-xs text-gray-400 mt-1">Be the first to support!</p>
                  </div>
                )}
              </div>

              {/* Disclaimer */}
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 text-center">
                <strong className="text-gray-700">Disclaimer:</strong> Views expressed are those of the campaigner.
              </div>
            </div>
          </div>
        </div>

        {/* ===== MOBILE LAYOUT ===== */}
        <div className="lg:hidden space-y-6">
          
          {/* 1. Campaign Title with Status Badge and Inactive Banner */}
          <div className="mb-2 text-left">
            <div className="flex items-center gap-2 mb-2 justify-start">
              <h1 className="text-2xl font-bold text-gray-900">
                {campaign.title}
              </h1>
              {campaign.status === "inactive" && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  Inactive
                </span>
              )}
            </div>
            
            {/* Inactive Campaign Banner - Mobile */}
            {campaign.status === "inactive" && (
              <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-yellow-800">Campaign Inactive</p>
                    <p className="text-xs text-yellow-700">This campaign is currently inactive and not accepting donations.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* 2. Image Gallery */}
          <div className="relative rounded-xl overflow-hidden shadow-lg">
            <div className="aspect-[16/9]">
              <img
                src={campaignImages[currentImageIndex]}
                alt={campaign.title}
                className="w-full h-full object-cover"
                onError={(e) => e.target.src = defaultCampaignImage}
              />
            </div>
            
            {/* Left Side Badges - Only Verified */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              <span className="px-2 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-[10px] font-medium border border-white/30 shadow-lg flex items-center gap-0.5 backdrop-blur-sm">
                <CheckBadgeIcon className="w-3 h-3" />
                Verified
              </span>
            </div>
            
            {/* Right Side Badge - Tax Benefit */}
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 bg-amber-500 text-white rounded-full text-[10px] font-medium shadow-lg flex items-center gap-0.5">
                <ShieldCheckIcon className="w-3 h-3" />
                Tax Benefit
              </span>
            </div>
            
            {hasMultipleImages && (
              <>
                <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full shadow-lg flex items-center justify-center">
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full shadow-lg flex items-center justify-center">
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </>
            )}
            
            {hasMultipleImages && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {campaignImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-white w-3' 
                        : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Donor Safety Promise Banner - Below Image Mobile - Aligned to Right */}
          <div className="mt-3 flex justify-end">
            <DonorSafetyBadge />
          </div>

          {/* 3. Support This Cause Card (Donation card) - MOVED UP */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-[#00B7B3] overflow-hidden">
            <div className="bg-gradient-to-r from-[#00B7B3] to-teal-500 p-4 text-white">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                  </span>
                  <span className="text-[10px] font-medium">
                    {campaign.days_left < 5 ? 'URGENT' : 'ACTIVE'}
                  </span>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    className="flex items-center gap-1 bg-white/20 backdrop-blur-sm hover:bg-white/30 px-2 py-1 rounded-full text-xs transition-all border border-white/30"
                  >
                    <span className="text-sm">{currencies.find(c => c.code === selectedCurrency)?.flag}</span>
                    <span className="font-medium">{selectedCurrency}</span>
                    <svg className={`w-3 h-3 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showCurrencyDropdown && (
                    <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-20 overflow-y-auto max-h-60 w-36 animate-fade-in">
                      {currencies.map((currency) => (
                        <button
                          key={currency.code}
                          onClick={() => handleCurrencyChange(currency.code)}
                          className={`w-full px-3 py-2.5 text-left flex items-center gap-2 transition-all hover:bg-gray-50 ${
                            selectedCurrency === currency.code ? 'bg-teal-50' : ''
                          }`}
                        >
                          <span className="text-lg">{currency.flag}</span>
                          <span className="text-sm font-medium text-gray-900">{currency.code}</span>
                          {selectedCurrency === currency.code && (
                            <CheckBadgeIcon className="w-3.5 h-3.5 text-[#00B7B3] ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <h3 className="text-base font-bold text-center">Your Support Can Save a Life Today</h3>
            </div>

            <div className="p-4">
              {/* Mobile Progress */}
              <div className="flex items-center justify-between mb-4">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="54" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                    <circle cx="64" cy="64" r="54" stroke="#00B7B3" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={2 * Math.PI * 54} strokeDashoffset={2 * Math.PI * 54 * (1 - progress / 100)} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold">{progress}%</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-bold text-gray-900">{formatCurrency(campaign.collected_amount)}</span>
                  <p className="text-sm text-gray-500">raised</p>
                  <p className="text-sm font-medium text-gray-700">of {formatCurrency(campaign.goal_amount)}</p>
                </div>
              </div>

              {/* Mobile Pay With */}
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 mb-2 text-center">Pay with</p>
                <div className="grid grid-cols-5 gap-1">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        onClick={() => {
                          setSelectedPaymentMethod(method.id);
                          setShowDonationForm(true);
                        }}
                        className="p-2 rounded-lg border border-gray-200 text-center flex flex-col items-center justify-center"
                      >
                        <Icon className="w-4 h-4 mx-auto text-gray-600" />
                        <span className="text-[8px] mt-1 text-gray-500">{method.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Fund Transfer Info Text - Right-aligned Popover */}
              <div className="mb-3 px-3 py-2.5 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-100 shadow-sm">
                <div className="flex items-center justify-start gap-2 text-xs font-medium text-gray-700">
                  <svg className="w-4 h-4 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Funds will be transferred for patient treatment</span>
                  <InfoPopover 
                    content="DonatePe prioritizes funds transfer towards hospital/healthcare service provider/medicine procurement, then towards reimbursement of patient's medical expenses and post-treatment expenses."
                    title="How funds are utilized"
                    position="top"
                  />
                </div>
              </div>

              {/* Mobile Donate Button - Always enabled */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={handleDonate}
                  className="px-8 py-3 bg-gradient-to-r from-[#00B7B3] to-teal-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 mx-auto"
                >
                  <HeartIcon className="w-4 h-4" />
                  Donate Now
                </button>
              </div>

              {/* Mobile Share Icons */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-600 mb-2 text-center">Share this campaign</p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {socialPlatforms.slice(0, 3).map((platform) => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.channel}
                        onClick={() => handleShare(platform)}
                        className="p-2 rounded-lg border border-gray-200 text-center flex flex-col items-center justify-center"
                      >
                        {platform.name === 'Facebook' && (
                          <svg className="w-5 h-5 mx-auto text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        )}
                        {platform.name === 'Twitter' && (
                          <svg className="w-5 h-5 mx-auto text-black" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231z"/>
                          </svg>
                        )}
                        {platform.name === 'WhatsApp' && Icon && (
                          <Icon className="w-5 h-5 mx-auto text-[#25D366]" />
                        )}
                        <span className="text-[8px] mt-1 text-gray-500">{platform.name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {socialPlatforms.slice(3, 6).map((platform) => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.channel}
                        onClick={() => handleShare(platform)}
                        className="p-2 rounded-lg border border-gray-200 text-center flex flex-col items-center justify-center"
                      >
                        {platform.name === 'Email' && Icon && (
                          <Icon className="w-5 h-5 mx-auto text-gray-600" />
                        )}
                        {platform.name === 'LinkedIn' && (
                          <svg className="w-5 h-5 mx-auto text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        )}
                        {platform.name === 'Telegram' && (
                          <svg className="w-5 h-5 mx-auto text-[#0088cc]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.916.49-1.305.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                          </svg>
                        )}
                        <span className="text-[8px] mt-1 text-gray-500">{platform.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Mobile Supporters Stats */}
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="text-center">{campaign.supporters}+ supporters</span>
                <span className="text-center">{campaign.days_left}d left</span>
              </div>
            </div>
          </div>

          {/* 4. Fundraiser Details Card - MOVED DOWN with updated content */}
<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
    <UserCircleIcon className="w-4 h-4 text-[#00B7B3]" />
    Fundraiser Details
  </h3>
  <div className="space-y-3">
    {/* Supporter */}
    <div className="flex gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {getInitials(campaign.creator_name)}
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 text-left">Supporter</span>
        <span className="text-sm font-medium text-gray-900 text-left">{campaign.creator_name}</span>
      </div>
    </div>
    
    {/* Beneficiary - Name only */}
    <div className="flex gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {getInitials(campaign.patient_name)}
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 text-left">Beneficiary</span>
        <span className="text-sm font-medium text-gray-900 text-left">{campaign.patient_name}</span>
      </div>
    </div>
    
    {/* Relationship & Hospital - Continuous line without dot */}
    <div className="mt-2 pt-2 border-t border-gray-100">
      <p className="text-xs text-gray-700 leading-relaxed text-left">
        {getRelationText()} Getting treatment at <span className="font-semibold">{campaign.hospital}</span>, {campaign.location}
      </p>
    </div>
  </div>
</div>

          {/* Mobile Tabs */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="flex border-b">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 text-xs font-medium text-center ${
                    activeTab === tab.id 
                      ? 'text-[#00B7B3] border-b-2 border-[#00B7B3]' 
                      : 'text-gray-500'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {activeTab === 'story' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-700 leading-relaxed text-left">{campaign.story}</p>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 text-left">Hospital</p>
                      <p className="text-sm font-medium text-left">{campaign.hospital}</p>
                      <p className="text-xs text-gray-500 text-left">{campaign.location}</p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 text-left">Patient</p>
                      <p className="text-sm font-medium text-left">{campaign.patient_name}</p>
                      <p className="text-xs text-gray-500 text-left">{campaign.patient_age}y • {campaign.patient_condition}</p>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'updates' && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No updates yet
                </div>
              )}
              {activeTab === 'documents' && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No documents available
                </div>
              )}
            </div>
          </div>

          {/* Mobile Top Contributors */}
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <SparklesIcon className="w-4 h-4 text-amber-500" />
              Top Contributors
            </h3>
            
            {loadingContributors ? (
              <div className="animate-pulse space-y-2">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ) : topContributors.length > 0 ? (
              <div className="space-y-2">
                {topContributors.slice(0, 3).map((contributor, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
                      }`}>
                        {getInitials(contributor.donor_name)}
                      </div>
                      <span className="text-sm">{contributor.donor_name}</span>
                    </div>
                    <span className="text-sm font-bold text-[#00B7B3]">{formatCurrency(contributor.total_amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 text-sm py-4">No contributors yet</p>
            )}
          </div>

          {/* Mobile Recent Donations */}
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <HeartIcon className="w-4 h-4 text-rose-500" />
              Recent Support
            </h3>
            
            {loadingRecentDonations ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-2 animate-pulse">
                    <div className="w-6 h-6 rounded-full bg-gray-200"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-20 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-12"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentDonations.length > 0 ? (
              <div className="space-y-3">
                {recentDonations.slice(0, 5).map((donation, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#00B7B3] to-teal-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {donation.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium text-gray-900 truncate">{donation.name}</p>
                        <span className="text-xs font-bold text-[#00B7B3] flex-shrink-0 ml-2">{formatCurrency(donation.amount)}</span>
                      </div>
                      <p className="text-xs text-gray-500">{donation.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <HeartIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No donations yet</p>
                <p className="text-xs text-gray-400 mt-1">Be the first to support!</p>
              </div>
            )}
          </div>

          {/* Mobile Disclaimer */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 text-center">
            <strong>Disclaimer:</strong> Views expressed are those of the campaigner.
          </div>
        </div>
      </main>

      {/* CTA SECTION */}
      <section className="relative overflow-hidden mt-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-emerald-600"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Ready to start your DonatePe journey?
          </h2>
          <p className="text-blue-100 mb-5 text-base max-w-2xl mx-auto">
            Join thousands of campaigners who've raised crores for what matters.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/start-fundraiser"
              className="group px-6 py-3 bg-white hover:bg-gray-100 text-gray-900 rounded-lg text-sm font-medium shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              <span className="flex items-center gap-2">
                Start your fundraiser
                <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </a>
          </div>
          
          <p className="text-xs text-blue-200 mt-4">
            ✓ Free forever · ✓ Instant withdrawals · ✓ 24/7 support
          </p>
        </div>
      </section>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .duration-7000 {
          transition-duration: 7000ms;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
    </>
  );
};

export default CampaignDetails;